import React, { useState, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import TextInput from '../../../shared/Inputs/TextInput/TextInput'
import SelectInput from '../../../shared/Inputs/SelectInput/SelectInput'
import {
  validateString,
  smartValidateString,
} from '../../../validate/validators'
import { Country, Lang } from '../../../types/common-types'
import styles from '../FiscalScreenOne.module.scss'
import { COUNTRY, INSTANCE } from '../../../server-parameters'
import { copyFromRegistered, residenceCopyFromFactual, residenceCopyFromRegistered } from '../../../utils/helpers'
import TroubleMessage from '../../../shared/TroubleMessage/TroubleMessage'
import { State } from '../../../state-actions-reducers'

interface Props {
  lang: Lang
  accountForm: any
  addressCountries: Country[]
  regionCityRegistered: any
  regionCityFactual: any
  regionCityResidence?: any  // only required for IP forms (F_FiscalType === '1')
}

export default function AddressInputs({
  lang,
  accountForm,
  addressCountries,
  regionCityRegistered,
  regionCityFactual,
  regionCityResidence,
}: Props) {
  const { watch, setValue, getValues, formState: { errors } } = useFormContext<State>()
  const state = watch()

  const priorityRegions = [
    ['Москва и Московская обл.', 'Мәскеу және Мәскеу облысы', 'Moscow and Moscow region'],
    ['Санкт-Петербург и область', 'Санкт-Петербург және облыс', 'St.Petersburg and Leningrad region'],
    ['Алматинская область', 'Алматы облысы', 'Almaty']
  ];

  const getPriorityRegions = (instance: string) => {
    if (instance === 'msk') {
      return [
        priorityRegions[0], // Москва
        priorityRegions[1], // СПб
        priorityRegions[2]  // Алматы
      ];
    } else if (instance === 'spb' || instance === 'second') {
      return [
        priorityRegions[1], // СПб
        priorityRegions[0], // Москва
        priorityRegions[2]  // Алматы
      ];
    }
    return priorityRegions; // по умолчанию оригинальный порядок
  };

  const regionRegisteredListUnigue = [
    ...new Set(regionCityRegistered.map((item: any) => item.region))
  ].sort((a, b) => {
    const currentPriorityRegions = getPriorityRegions(INSTANCE);

    // Сначала сортируем по приоритету регионов
    for (const regionGroup of currentPriorityRegions) {
      const aInGroup = regionGroup.includes(a as string);
      const bInGroup = regionGroup.includes(b as string);
      if (aInGroup && !bInGroup) return -1;
      if (!aInGroup && bInGroup) return 1;
      if (aInGroup && bInGroup) break;
    }

    // Затем сортируем по алфавиту
    return (a as string).localeCompare(b as string);
  });

  const cityRegisteredListForRegion = regionCityRegistered.filter(
    (item: any) => {
      if (item.region === state.addressRegisteredRegion) return item
    }
  )
  const regionRegisteredList = regionRegisteredListUnigue.map((item: any) => {
    return {
      value: item,
      label: {
        rus: item,
        eng: item,
        kz: item,
      },
    }
  }).filter((e) => (e.value !== 'Республика Крым и г. Севастополь' && INSTANCE === 'ala') || INSTANCE !== 'ala')
  const cityRegisteredList = cityRegisteredListForRegion
    ? cityRegisteredListForRegion
      .map((item: any) => {
        return {
          value: item.city,
          label: {
            rus: item.city,
            eng: item.city,
            kz: item.city,
          },
        }
      })
      .sort(
        (
          a: { value: string; label: string },
          b: { value: string; label: string }
        ) => {
          if (a.value.toLowerCase() < b.value.toLowerCase()) {
            return -1
          }
          if (a.value.toLowerCase() > b.value.toLowerCase()) {
            return 1
          }
          return 0
        }
      ).sort((x: any, y: any) =>
        ['Москва', 'Мәскеу', 'Moscow'].includes(x.value)
          ? -1
          : 1
      ).sort((x: any, y: any) =>
        ['Санкт-Петербург', 'Санкт-Петербург', 'Saint-Petersburg'].includes(x.value)
          ? -1
          : 1
      ).sort((x: any, y: any) =>
        ['Алматы', 'Алматы', 'Almaty'].includes(x.value)
          ? -1
          : 1
      )
    : []

  //#region Factual List

  const regionFactualListUnigue = [
    ...new Set(regionCityFactual.map((item: any) => item.region))
  ].sort((a, b) => {
    const currentPriorityRegions = getPriorityRegions(INSTANCE);

    // Сначала сортируем по приоритету регионов
    for (const regionGroup of currentPriorityRegions) {
      const aInGroup = regionGroup.includes(a as string);
      const bInGroup = regionGroup.includes(b as string);
      if (aInGroup && !bInGroup) return -1;
      if (!aInGroup && bInGroup) return 1;
      if (aInGroup && bInGroup) break;
    }

    // Затем сортируем по алфавиту
    return (a as string).localeCompare(b as string);
  });
  const cityFactualListForRegion = regionCityFactual.filter((item: any) => {
    if (item.region === state.addressFactualRegion) return item
  })
  const regionFactualList = regionFactualListUnigue.map((item: any) => {
    return {
      value: item,
      label: {
        rus: item,
        eng: item,
        kz: item,
      },
    }
  }).filter((e) => (e.value !== 'Республика Крым и г. Севастополь' && INSTANCE === 'ala') || INSTANCE !== 'ala')
  const cityFactualList = cityFactualListForRegion
    ? cityFactualListForRegion
      .map((item: any) => {
        return {
          value: item.city,
          label: {
            rus: item.city,
            eng: item.city,
            kz: item.city,
          },
        }
      })
      .sort(
        (
          a: { value: string; label: string },
          b: { value: string; label: string }
        ) => {
          if (a.value.toLowerCase() < b.value.toLowerCase()) {
            return -1
          }
          if (a.value.toLowerCase() > b.value.toLowerCase()) {
            return 1
          }
          return 0
        }
      ).sort((x: any, y: any) =>
        ['Москва', 'Мәскеу', 'Moscow'].includes(x.value)
          ? -1
          : 1
      ).sort((x: any, y: any) =>
        ['Санкт-Петербург', 'Санкт-Петербург', 'Saint-Petersburg'].includes(x.value)
          ? -1
          : 1
      ).sort((x: any, y: any) =>
        ['Алматы', 'Алматы', 'Almaty'].includes(x.value)
          ? -1
          : 1
      )
    : []

  //#endregion
  //#region Residence List
  const regionResidenceListUnigue = [
    ...new Set(regionCityFactual.map((item: any) => item.region))
  ].sort((a, b) => {
    const currentPriorityRegions = getPriorityRegions(INSTANCE);

    // Сначала сортируем по приоритету регионов
    for (const regionGroup of currentPriorityRegions) {
      const aInGroup = regionGroup.includes(a as string);
      const bInGroup = regionGroup.includes(b as string);
      if (aInGroup && !bInGroup) return -1;
      if (!aInGroup && bInGroup) return 1;
      if (aInGroup && bInGroup) break;
    }

    // Затем сортируем по алфавиту
    return (a as string).localeCompare(b as string);
  });
  const cityResidenceListForRegion = regionCityResidence?.filter((item: any) => {
    if (item.region === state.addressResidenceRegion) return item
  })
  const regionResidenceList = regionResidenceListUnigue.map((item: any) => {
    return {
      value: item,
      label: {
        rus: item,
        eng: item,
        kz: item,
      },
    }
  })
  const cityResidenceList = cityResidenceListForRegion
    ? cityResidenceListForRegion
      .map((item: any) => {
        return {
          value: item.city,
          label: {
            rus: item.city,
            eng: item.city,
            kz: item.city,
          },
        }
      })
      .sort(
        (
          a: { value: string; label: string },
          b: { value: string; label: string }
        ) => {
          if (a.value.toLowerCase() < b.value.toLowerCase()) {
            return -1
          }
          if (a.value.toLowerCase() > b.value.toLowerCase()) {
            return 1
          }
          return 0
        }
      ).sort((x: any, y: any) =>
        ['Москва', 'Мәскеу', 'Moscow'].includes(x.value)
          ? -1
          : 1
      ).sort((x: any, y: any) =>
        ['Санкт-Петербург', 'Санкт-Петербург', 'Saint-Petersburg'].includes(x.value)
          ? -1
          : 1
      ).sort((x: any, y: any) =>
        ['Алматы', 'Алматы', 'Almaty'].includes(x.value)
          ? -1
          : 1
      )
    : []
  //#endregion

  const [disabledAddress, setDisabledAddress] = useState(false)
  const isResidenceAddressDisabled = state.addressResidenceCopyFromRegistered || state.addressResidenceCopyFromFactual

  useEffect(() => {
    if (state.addressFactualCopyFromRegistered) setDisabledAddress(true)
    if (
      state.addressRegisteredRegion.length > 0 &&
      !regionRegisteredList.find(item => {
        return item.value === state.addressRegisteredRegion
      })
    ) {
      setValue('addressRegisteredRegion', '', { shouldDirty: true })
      setValue('addressRegisteredCity', '', { shouldDirty: true })
    }
    if (
      state.addressFactualRegion.length > 0 &&
      !regionFactualList.find(item => {
        return item.value === state.addressFactualRegion
      })
    ) {
      setValue('addressFactualRegion', '', { shouldDirty: true })
      setValue('addressFactualCity', '', { shouldDirty: true })
    }
  }, [])

  return (
    <>
      {/* REGISTRATION ADDRESS */}
      <div className="form-group">
        <div className="row">
          <div className="col-12 col-xs-12">
            <h3>{accountForm.stepAddressRegisteredFiscal[lang]}</h3>
          </div>
          <div className="col-4 col-xs-12">
            {/* REGISTRATION ADDRESS COUNTRY */}
            <SelectInput
              testName={'addressRegisteredCountry'}
              testErrorName={'error-addressRegisteredCountry'}
              noOptionsMessage={accountForm.citizenshipInput.placeholder}
              lang={lang}
              disabled={!state.agreed}
              content={accountForm.addressCountry}
              options={addressCountries}
              onChange={id => {
                setValue('addressRegisteredCountry', id, { shouldDirty: true })
                setValue('addressRegisteredRegion', '', { shouldDirty: true })
                setValue('addressRegisteredCity', '', { shouldDirty: true })
                if (disabledAddress)
                  setValue('addressFactualCountry', id, { shouldDirty: true })
                if (state.addressResidenceCopyFromRegistered || (disabledAddress && state.addressResidenceCopyFromFactual))
                  setValue('addressResidenceCountry', id, { shouldDirty: true })
              }}
              valid={
                !errors.addressRegisteredCountry &&
                validateString(state.addressRegisteredCountry, 300)
              }
              value={state.addressRegisteredCountry}
              prefix={
                state.iframeCountry === 'kz'
                  ? 'urRegisteredAddressCountry'
                  : 'urRegisteredAddressCountryRf'
              }
            />
          </div>
          <div className="col-4 col-xs-12">
            {/* REGISTRATION ADDRESS REGION */}
            {regionRegisteredList.length > 0 ? (
              <SelectInput
                testName={'addressRegisteredRegion'}
                testErrorName={'error-addressRegisteredRegion'}
                lang={lang}
                noOptionsMessage={accountForm.addressRegion.placeholder}
                content={accountForm.addressRegion}
                value={state.addressRegisteredRegion}
                options={regionRegisteredList}
                valid={
                  state.addressRegisteredRegion.length > 0
                    ? smartValidateString(state.addressRegisteredRegion) &&
                    !errors.addressRegisteredRegion
                    : !errors.addressRegisteredRegion
                }
                onChange={addressRegisteredRegion => {
                  setValue('addressRegisteredRegion', addressRegisteredRegion, { shouldDirty: true })
                  setValue('addressRegisteredCity', '', { shouldDirty: true })
                  if (disabledAddress)
                    setValue('addressFactualRegion', addressRegisteredRegion, { shouldDirty: true })
                  if (state.addressResidenceCopyFromRegistered || (disabledAddress && state.addressResidenceCopyFromFactual))
                    setValue('addressResidenceRegion', addressRegisteredRegion, { shouldDirty: true })
                }}
                className={styles.input}
                prefix={
                  state.addressRegisteredCountry === '398'
                    ? 'urRegisteredAddressRegion' :
                    state.addressRegisteredCountry === '643'
                      ? 'urRegisteredAddressRegionRf'
                      : undefined
                }
              />
            ) : (
              <TextInput
                testName="addressRegisteredRegion"
                testErrorName="error-addressRegisteredRegion"
                disabled={!state.agreed}
                lang={lang}
                content={accountForm.addressRegion}
                onChange={addressRegisteredRegion => {
                  setValue('addressRegisteredRegion', addressRegisteredRegion, { shouldDirty: true })
                  if (disabledAddress)
                    setValue('addressFactualRegion', addressRegisteredRegion, { shouldDirty: true })
                  if (state.addressResidenceCopyFromRegistered || (disabledAddress && state.addressResidenceCopyFromFactual))
                    setValue('addressResidenceRegion', addressRegisteredRegion, { shouldDirty: true })
                }}
                valid={
                  state.addressRegisteredRegion.length > 0
                    ? smartValidateString(state.addressRegisteredRegion) &&
                    !errors.addressRegisteredRegion
                    : !errors.addressRegisteredRegion
                }
                value={state.addressRegisteredRegion}
              />
            )}
          </div>
          <div className="col-4 col-xs-12">
            {/* REGISTRATION ADDRESS CITY */}
            {regionRegisteredList.length > 0 ? (
              <SelectInput
                testName={'addressRegisteredCity'}
                testErrorName={'error-addressRegisteredCity'}
                disabled={!state.addressRegisteredRegion}
                lang={lang}
                noOptionsMessage={accountForm.addressCity.placeholder}
                content={accountForm.addressCity}
                value={state.addressRegisteredCity}
                optionValue={{
                  value: state.addressRegisteredCity,
                  label: state.addressRegisteredCity
                }}
                options={cityRegisteredList}
                valid={
                  state.addressRegisteredCity.length > 0
                    ? smartValidateString(state.addressRegisteredCity) &&
                    !errors.addressRegisteredCity
                    : !errors.addressRegisteredCity
                }
                onChange={addressRegisteredCity => {
                  setValue('addressRegisteredCity', addressRegisteredCity, { shouldDirty: true })
                  if (disabledAddress)
                    setValue('addressFactualCity', addressRegisteredCity, { shouldDirty: true })
                  if (state.addressResidenceCopyFromRegistered || (disabledAddress && state.addressResidenceCopyFromFactual))
                    setValue('addressResidenceCity', addressRegisteredCity, { shouldDirty: true })
                }}
                onCreateOption={
                  (addressRegisteredCity: any) => {
                    setValue('addressRegisteredCity', addressRegisteredCity, { shouldDirty: true })
                    if (disabledAddress)
                      setValue('addressFactualCity', addressRegisteredCity, { shouldDirty: true })
                  }
                }
                className={styles.input}
                prefix={
                  state.addressRegisteredCountry === '398' && ['Алматинская область', 'Алматы облысы', 'Almaty'].includes(state.addressRegisteredRegion)
                    ? 'urRegisteredAddressCity'
                    : state.addressRegisteredCountry === '643' && ['Москва и Московская обл.', 'Мәскеу және Мәскеу облысы', 'Moscow and Moscow region', 'Санкт-Петербург и область', 'Санкт-Петербург және облыс', 'St.Petersburg and Leningrad region'].includes(state.addressRegisteredRegion)
                      ? 'urRegisteredAddressCityRf'
                      : undefined
                }
                isSearchable={true}
                creatable={true}
              />
            ) : (
              <TextInput
                testName="addressRegisteredCity"
                testErrorName="error-addressRegisteredCity"
                disabled={!state.agreed}
                lang={lang}
                content={accountForm.addressCity}
                onChange={addressRegisteredCity => {
                  setValue('addressRegisteredCity', addressRegisteredCity, { shouldDirty: true })
                  if (disabledAddress)
                    setValue('addressFactualCity', addressRegisteredCity, { shouldDirty: true })
                  if (state.addressResidenceCopyFromRegistered || (disabledAddress && state.addressResidenceCopyFromFactual))
                    setValue('addressResidenceCity', addressRegisteredCity, { shouldDirty: true })
                }}
                valid={
                  state.addressRegisteredCity.length > 0
                    ? smartValidateString(state.addressRegisteredCity) &&
                    !errors.addressRegisteredCity
                    : smartValidateString(state.addressRegisteredCity)
                }
                value={state.addressRegisteredCity}
              />
            )}
          </div>
          <div className="col-3 col-xs-12">
            {/* REGISTRATION ADDRESS STREET */}
            <TextInput
              testName="addressRegisteredStreet"
              testErrorName="error-addressRegisteredStreet"
              disabled={!state.agreed}
              lang={lang}
              content={accountForm.addressStreet}
              onChange={addressRegisteredStreet => {
                setValue('addressRegisteredStreet', addressRegisteredStreet, { shouldDirty: true })
                if (disabledAddress)
                  setValue('addressFactualStreet', addressRegisteredStreet, { shouldDirty: true })
                if (state.addressResidenceCopyFromRegistered || (disabledAddress && state.addressResidenceCopyFromFactual))
                  setValue('addressResidenceStreet', addressRegisteredStreet, { shouldDirty: true })
              }}
              valid={
                state.addressRegisteredStreet.length > 0
                  ? smartValidateString(state.addressRegisteredStreet) &&
                  !errors.addressRegisteredStreet
                  : !errors.addressRegisteredStreet
              }
              value={state.addressRegisteredStreet}
            />
          </div>
          <div className="col-1 col-xs-12">
            {/* REGISTRATION ADDRESS BUILDING */}
            <TextInput
              testName="addressRegisteredBuilding"
              testErrorName="error-addressRegisteredBuilding"
              disabled={!state.agreed}
              lang={lang}
              content={accountForm.addressBuilding}
              onChange={addressRegisteredBuilding => {
                setValue('addressRegisteredBuilding', addressRegisteredBuilding, { shouldDirty: true })
                if (disabledAddress)
                  setValue('addressFactualBuilding', addressRegisteredBuilding, { shouldDirty: true })
                if (state.addressResidenceCopyFromRegistered || (disabledAddress && state.addressResidenceCopyFromFactual))
                  setValue('addressResidenceBuilding', addressRegisteredBuilding, { shouldDirty: true })
              }}
              valid={
                validateString(state.addressRegisteredBuilding, 100) &&
                !errors.addressRegisteredBuilding
              }
              value={state.addressRegisteredBuilding}
            />
          </div>
          <div className="col-1 col-xs-12">
            {/* REGISTRATION ADDRESS BUILDING BLOCK */}
            <TextInput
              testName="addressRegisteredBuildingBlock"
              testErrorName="error-addressRegisteredBuildingBlock"
              disabled={!state.agreed}
              lang={lang}
              content={accountForm.addressBuildingBlock}
              onChange={addressRegisteredBuildingBlock => {
                setValue('addressRegisteredBuildingBlock', addressRegisteredBuildingBlock, { shouldDirty: true })
                if (disabledAddress)
                  setValue('addressFactualBuildingBlock', addressRegisteredBuildingBlock, { shouldDirty: true })
                if (state.addressResidenceCopyFromRegistered || (disabledAddress && state.addressResidenceCopyFromFactual))
                  setValue('addressResidenceBuildingBlock', addressRegisteredBuildingBlock, { shouldDirty: true })
              }}
              valid={
                validateString(state.addressRegisteredBuildingBlock, 100) &&
                !errors.addressRegisteredBuildingBlock
              }
              value={state.addressRegisteredBuildingBlock}
            />
          </div>
          {state.F_FiscalType !== '1' ? <>
            <div className="col-2 col-xs-12">
              {/* REGISTRATION ADDRESS PREMISES */}
              <TextInput
                testName="addressRegisteredPremises"
                testErrorName="error-addressRegisteredPremises"
                disabled={!state.agreed}
                lang={lang}
                content={accountForm.addressPremises}
                onChange={addressRegisteredPremises => {
                  setValue('addressRegisteredPremises', addressRegisteredPremises, { shouldDirty: true })
                  if (disabledAddress)
                    setValue('addressFactualPremises', addressRegisteredPremises, { shouldDirty: true })
                  if (state.addressResidenceCopyFromRegistered || (disabledAddress && state.addressResidenceCopyFromFactual))
                    setValue('addressResidencePremises', addressRegisteredPremises, { shouldDirty: true })
                }}
                valid={
                  validateString(state.addressRegisteredPremises, 100) &&
                  !errors.addressRegisteredPremises
                }
                value={state.addressRegisteredPremises}
              />
            </div>
            <div className="col-1 col-xs-12">
              {/* REGISTRATION ADDRESS FLOOR */}
              <TextInput
                testName="addressRegisteredFloor"
                testErrorName="error-addressRegisteredFloor"
                disabled={!state.agreed}
                lang={lang}
                content={accountForm.addressFloor}
                onChange={addressRegisteredFloor => {
                  setValue('addressRegisteredFloor', addressRegisteredFloor, { shouldDirty: true })
                  if (disabledAddress)
                    setValue('addressFactualFloor', addressRegisteredFloor, { shouldDirty: true })
                  if (state.addressResidenceCopyFromRegistered || (disabledAddress && state.addressResidenceCopyFromFactual))
                    setValue('addressResidenceFloor', addressRegisteredFloor, { shouldDirty: true })
                }}
                valid={
                  validateString(state.addressRegisteredFloor, 100) &&
                  !errors.addressRegisteredFloor
                }
                value={state.addressRegisteredFloor}
              />
            </div>
            <div className="col-2 col-xs-12">
              {/* REGISTRATION ADDRESS OFFICE */}
              <TextInput
                testName="addressRegisteredOffice"
                testErrorName="error-addressRegisteredOffice"
                disabled={!state.agreed}
                lang={lang}
                content={accountForm.addressOffice}
                onChange={addressRegisteredOffice => {
                  setValue('addressRegisteredOffice', addressRegisteredOffice, { shouldDirty: true })
                  if (disabledAddress)
                    setValue('addressFactualOffice', addressRegisteredOffice, { shouldDirty: true })
                  if (state.addressResidenceCopyFromRegistered || (disabledAddress && state.addressResidenceCopyFromFactual))
                    setValue('addressResidenceOffice', addressRegisteredOffice, { shouldDirty: true })
                }}
                valid={
                  validateString(state.addressRegisteredOffice, 100) &&
                  !errors.addressRegisteredOffice
                }
                value={state.addressRegisteredOffice}
              />
            </div>
          </> : <div className="col-1 col-xs-12">
            {/* REGISTRATION ADDRESS APARTMENTS */}
            <TextInput
              testName="addressRegisteredApartments"
              testErrorName="error-addressRegisteredApartments"
              disabled={!state.agreed}
              lang={lang}
              content={accountForm.addressApartments}
              onChange={addressRegisteredApartments => {
                setValue('addressRegisteredApartments', addressRegisteredApartments, { shouldDirty: true })
                if (disabledAddress)
                  setValue('addressFactualApartments', addressRegisteredApartments, { shouldDirty: true })
                if (state.addressResidenceCopyFromRegistered || (disabledAddress && state.addressResidenceCopyFromFactual))
                  setValue('addressResidenceApartments', addressRegisteredApartments, { shouldDirty: true })
              }}
              valid={
                validateString(state.addressRegisteredApartments, 50) &&
                !errors.addressRegisteredApartments
              }
              value={state.addressRegisteredApartments}
            />
          </div>}

          <div className="col-2 col-xs-12">
            {/* REGISTERED POST CODE */}
            <TextInput
              testName="addressRegisteredPostCode"
              testErrorName="error-addressRegisteredPostCode"
              disabled={!state.agreed}
              lang={lang}
              content={accountForm.F_Adress_Index}
              onChange={addressRegisteredPostCode => {
                setValue('addressRegisteredPostCode', addressRegisteredPostCode.replace(/_/gim, ''), { shouldDirty: true })
                if (disabledAddress)
                  setValue('addressFactualPostCode', addressRegisteredPostCode.replace(/_/gim, ''), { shouldDirty: true })
                if (state.addressResidenceCopyFromRegistered || (disabledAddress && state.addressResidenceCopyFromFactual))
                  setValue('addressResidencePostCode', addressRegisteredPostCode.replace(/_/gim, ''), { shouldDirty: true })
              }}
              valid={
                validateString(state.addressRegisteredPostCode, 50) &&
                !errors.addressRegisteredPostCode
              }
              mask={'*******'}
              required
              value={state.addressRegisteredPostCode}
            />
          </div>
          <div className="col-12 col-xs-12">
            <TroubleMessage lang={lang} accountForm={accountForm} />
          </div>
        </div>
      </div>
      {/* FACTUAL ADDRESS */}
      <div className="form-group">
        <div className="row">
          <div className="col-12 col-xs-12"></div>
          <div className="col-12 col-xs-12">
            <h3>{accountForm.stepAddressFactualFiscal[lang]}</h3>
          </div>
          {state.F_FiscalType !== '1' && <div className={styles.checkInputBox + ' ' + 'col-12 col-xs-12'}>
            <input
              data-test-id='addressFactualCopyFromRegistered'
              id="copyInput"
              name="copyInput"
              type="checkBox"
              onChange={e => {
                const current = getValues()
                const next = copyFromRegistered(current as State)
                Object.keys(next).forEach(key => {
                  if ((next as any)[key] !== (current as any)[key]) {
                    setValue(key as any, (next as any)[key], { shouldDirty: true })
                  }
                })
                setDisabledAddress(e.target.checked)
              }}
              checked={state.addressFactualCopyFromRegistered}
            />
            <label htmlFor="copyInput">
              {accountForm.fiscalAddressFactualCopyBtnDesc[lang]}
            </label>
          </div>}
          <div className="col-4 col-xs-12">
            {/* FACTUAL ADDRESS COUNTRY */}
            <SelectInput
              testName={'addressFactualCountry'}
              testErrorName={'error-addressFactualCountry'}
              noOptionsMessage={accountForm.citizenshipInput.placeholder}
              lang={lang}
              disabled={!state.agreed || disabledAddress}
              content={accountForm.addressCountry}
              options={addressCountries}
              onChange={id => {
                setValue('addressFactualCountry', id, { shouldDirty: true })
                setValue('addressFactualRegion', '', { shouldDirty: true })
                setValue('addressFactualCity', '', { shouldDirty: true })
                if (state.addressResidenceCopyFromFactual)
                  setValue('addressResidenceCountry', id, { shouldDirty: true })
              }}
              valid={
                !errors.addressFactualCountry &&
                validateString(state.addressFactualCountry, 3, false)
              }
              value={state.addressFactualCountry}
              prefix={
                state.iframeCountry === 'kz'
                  ? 'urFactualAddressCountry'
                  : 'urFactualAddressCountryRf'
              }
            />
          </div>
          <div className="col-4 col-xs-12">
            {/* FACTUAL ADDRESS REGION */}
            {regionFactualList.length > 0 ? (
              <SelectInput
                testName={'addressFactualRegion'}
                testErrorName={'error-addressFactualRegion'}
                disabled={disabledAddress}
                className={styles.input}
                lang={lang}
                noOptionsMessage={accountForm.addressRegion.placeholder}
                content={accountForm.addressRegion}
                value={state.addressFactualRegion}
                options={regionFactualList}
                valid={
                  state.addressFactualRegion.length > 0
                    ? smartValidateString(state.addressFactualRegion) &&
                    !errors.addressFactualRegion
                    : !errors.addressFactualRegion
                }
                onChange={addressFactualRegion => {
                  setValue('addressFactualRegion', addressFactualRegion, { shouldDirty: true })
                  setValue('addressFactualCity', '', { shouldDirty: true })
                  if (state.addressResidenceCopyFromFactual)
                    setValue('addressResidenceRegion', addressFactualRegion, { shouldDirty: true })
                }}
                prefix={
                  state.addressFactualCountry === '398'
                    ? 'urFactualAddressRegion' :
                    state.addressFactualCountry === '643'
                      ? 'urFactualAddressRegionRf'
                      : undefined
                }
              />
            ) : (
              <TextInput
                testName="addressFactualRegion"
                testErrorName="error-addressFactualRegion"
                disabled={!state.agreed || disabledAddress}
                lang={lang}
                content={accountForm.addressRegion}
                onChange={addressFactualRegion => {
                  setValue('addressFactualRegion', addressFactualRegion, { shouldDirty: true })
                  if (state.addressResidenceCopyFromFactual)
                    setValue('addressResidenceRegion', addressFactualRegion, { shouldDirty: true })
                }}
                valid={
                  state.addressFactualRegion.length > 0
                    ? smartValidateString(state.addressFactualRegion) &&
                    !errors.addressFactualRegion
                    : !errors.addressFactualRegion
                }
                value={state.addressFactualRegion}
              />
            )}
          </div>
          <div className="col-4 col-xs-12">
            {/* FACTUAL ADDRESS CITY */}
            {regionCityFactual ? (
              <SelectInput
                testName={'addressFactualCity'}
                testErrorName={'error-addressFactualCity'}
                className={styles.input}
                disabled={!state.addressFactualRegion || disabledAddress}
                lang={lang}
                noOptionsMessage={accountForm.addressCity.placeholder}
                content={accountForm.addressCity}
                value={state.addressFactualCity}
                optionValue={{
                  value: state.addressFactualCity,
                  label: state.addressFactualCity
                }}
                options={cityFactualList}
                valid={
                  state.addressFactualCity.length > 0
                    ? smartValidateString(state.addressFactualCity) &&
                    !errors.addressFactualCity
                    : !errors.addressFactualCity
                }
                onChange={addressFactualCity => {
                  setValue('addressFactualCity', addressFactualCity, { shouldDirty: true })
                  if (state.addressResidenceCopyFromFactual)
                    setValue('addressResidenceCity', addressFactualCity, { shouldDirty: true })
                }}
                onCreateOption={(addressFactualCity: any) => {
                  console.log('addressFactualCity', addressFactualCity)
                  setValue('addressFactualCity', addressFactualCity, { shouldDirty: true })
                }}
                prefix={
                  state.addressRegisteredCountry === '398' && ['Алматинская область', 'Алматы облысы', 'Almaty'].includes(state.addressFactualRegion)
                    ? 'urFactualAddressCity'
                    : state.addressFactualCountry === '643' && ['Москва и Московская обл.', 'Мәскеу және Мәскеу облысы', 'Moscow and Moscow region', 'Санкт-Петербург и область', 'Санкт-Петербург және облыс', 'St.Petersburg and Leningrad region'].includes(state.addressFactualRegion)
                      ? 'urFactualAddressCityRf'
                      : undefined
                }
                isSearchable={true}
                creatable={true}
              />
            ) : (
              <TextInput
                testName="addressFactualCity"
                testErrorName="error-addressFactualCity"
                disabled={disabledAddress}
                lang={lang}
                content={accountForm.addressCity}
                onChange={addressFactualCity => {
                  setValue('addressFactualCity', addressFactualCity, { shouldDirty: true })
                  if (state.addressResidenceCopyFromFactual)
                    setValue('addressResidenceCity', addressFactualCity, { shouldDirty: true })
                }}
                valid={
                  validateString(state.addressFactualCity, 300) &&
                  !errors.addressFactualCity
                }
                value={state.addressFactualCity}
                required
              />
            )}
          </div>
          <div className="col-3 col-xs-12">
            {/* FACTUAL ADDRESS STREET */}
            <TextInput
              testName="addressFactualStreet"
              testErrorName="error-addressFactualStreet"
              disabled={!state.agreed || disabledAddress}
              lang={lang}
              content={accountForm.addressStreet}
              onChange={addressFactualStreet => {
                setValue('addressFactualStreet', addressFactualStreet, { shouldDirty: true })
                if (state.addressResidenceCopyFromFactual)
                  setValue('addressResidenceStreet', addressFactualStreet, { shouldDirty: true })
              }}
              valid={
                state.addressFactualStreet.length > 0
                  ? smartValidateString(state.addressFactualStreet) &&
                  !errors.addressFactualStreet
                  : !errors.addressFactualStreet
              }
              value={state.addressFactualStreet}
            />
          </div>
          <div className="col-1 col-xs-12">
            {/* FACTUAL ADDRESS BUILDING */}
            <TextInput
              testName="addressFactualBuilding"
              testErrorName="error-addressFactualBuilding"
              disabled={!state.agreed || disabledAddress}
              lang={lang}
              content={accountForm.addressBuilding}
              onChange={addressFactualBuilding => {
                setValue('addressFactualBuilding', addressFactualBuilding, { shouldDirty: true })
                if (state.addressResidenceCopyFromFactual)
                  setValue('addressResidenceBuilding', addressFactualBuilding, { shouldDirty: true })
              }}
              valid={
                validateString(state.addressFactualBuilding, 300) &&
                !errors.addressFactualBuilding
              }
              value={state.addressFactualBuilding}
              required
            />
          </div>
          <div className="col-1 col-xs-12">
            {/* FACTUAL ADDRESS BUILDING BLOCK */}
            <TextInput
              testName="addressFactualBuildingBlock"
              testErrorName="error-addressFactualBuildingBlock"
              disabled={!state.agreed || disabledAddress}
              lang={lang}
              content={accountForm.addressBuildingBlock}
              onChange={addressFactualBuildingBlock => {
                setValue('addressFactualBuildingBlock', addressFactualBuildingBlock, { shouldDirty: true })
                if (state.addressResidenceCopyFromFactual)
                  setValue('addressResidenceBuildingBlock', addressFactualBuildingBlock, { shouldDirty: true })
              }}
              valid={
                validateString(state.addressFactualBuildingBlock, 100) &&
                !errors.addressFactualBuildingBlock
              }
              value={state.addressFactualBuildingBlock}
            />
          </div>
          <div className="col-2 col-xs-12">
            {/* FACTUAL ADDRESS PREMISES */}
            <TextInput
              testName="addressFactualPremises"
              testErrorName="error-addressFactualPremises"
              disabled={!state.agreed || disabledAddress}
              lang={lang}
              content={accountForm.addressPremises}
              onChange={addressFactualPremises => {
                setValue('addressFactualPremises', addressFactualPremises, { shouldDirty: true })
                if (state.addressResidenceCopyFromFactual)
                  setValue('addressResidencePremises', addressFactualPremises, { shouldDirty: true })
              }}
              valid={
                validateString(state.addressFactualPremises, 100) &&
                !errors.addressFactualPremises
              }
              value={state.addressFactualPremises}
            />
          </div>
          <div className="col-1 col-xs-12">
            {/* FACTUAL ADDRESS FLOOR */}
            <TextInput
              testName="addressFactualFloor"
              testErrorName="error-addressFactualFloor"
              disabled={!state.agreed || disabledAddress}
              lang={lang}
              content={accountForm.addressFloor}
              onChange={addressFactualFloor => {
                setValue('addressFactualFloor', addressFactualFloor, { shouldDirty: true })
                if (state.addressResidenceCopyFromFactual)
                  setValue('addressResidenceFloor', addressFactualFloor, { shouldDirty: true })
              }}
              valid={
                validateString(state.addressFactualFloor, 100) &&
                !errors.addressFactualFloor
              }
              value={state.addressFactualFloor}
            />
          </div>
          <div className="col-2 col-xs-12">
            {/* FACTUAL ADDRESS OFFICE */}
            <TextInput
              testName="addressFactualOffice"
              testErrorName="error-addressFactualOffice"
              disabled={!state.agreed || disabledAddress}
              lang={lang}
              content={state.F_FiscalType === '1' ? accountForm.addressOfficeAppartments : accountForm.addressOffice}
              onChange={addressFactualOffice => {
                setValue('addressFactualOffice', addressFactualOffice, { shouldDirty: true })
                if (state.addressResidenceCopyFromFactual)
                  setValue('addressFactualFloor', addressFactualOffice, { shouldDirty: true })
              }}
              valid={
                validateString(state.addressFactualOffice, 100) &&
                !errors.addressFactualOffice
              }
              value={state.addressFactualOffice}
            />
          </div>
          {/* <div className="col-1 col-xs-12"> */}
          {/* FACTUAL ADDRESS APARTMENTS */}
          {/* <TextInput
              testName="addressFactualApartments"
              testErrorName="error-addressFactualApartments"
              disabled={!state.agreed || disabledAddress}
              lang={lang}
              content={accountForm.F_Office_Number}
              onChange={addressFactualApartments => {
                setValue('addressFactualApartments', addressFactualApartments, { shouldDirty: true })
                if(state.addressResidenceCopyFromFactual)
                  setValue('addressResidenceApartments', addressFactualApartments, { shouldDirty: true })
              }}
              valid={
                validateString(state.addressFactualApartments, 50) &&
                !errors.addressFactualApartments
              }
              required
              value={state.addressFactualApartments}
            /> */}
          {/* </div> */}
          <div className="col-2 col-xs-12">
            {/* FACTUAL POST CODE */}
            <TextInput
              testName="addressFactualPostCode"
              testErrorName="error-addressFactualPostCode"
              disabled={!state.agreed || disabledAddress}
              lang={lang}
              content={accountForm.F_Adress_Index}
              onChange={addressFactualPostCode => {
                setValue('addressFactualPostCode', addressFactualPostCode.replace(/_/gim, ''), { shouldDirty: true })
                if (state.addressResidenceCopyFromFactual)
                  setValue('addressResidencePostCode', addressFactualPostCode, { shouldDirty: true })
              }}
              mask={'*******'}
              valid={
                validateString(state.addressFactualPostCode, 50) &&
                !errors.addressFactualPostCode
              }
              required
              value={state.addressFactualPostCode}
            />
          </div>
        </div>
      </div>
      {/* RESIDENCE ADDRESS */}
      {state.F_FiscalType === '1' && COUNTRY === 'rf' ?
        <div className="form-group">
          <div className="row">
            <div className="col-12 col-xs-12"></div>
            <div className="col-12 col-xs-12">
              <h3>{accountForm.stepAddressResidenceFiscal[lang]}</h3>
            </div>
            <div className={styles.checkInputBox + ' ' + 'col-12 col-xs-12'}>
              {state.F_FiscalType !== '1' && <>
                <input
                  data-test-id='residenseAddressCopyFromFactual'
                  id="copyInput_res_fact"
                  name="copyInput"
                  type="checkBox"
                  onChange={e => {
                    const current = getValues()
                    const next = residenceCopyFromFactual(current as State)
                    Object.keys(next).forEach(key => {
                      if ((next as any)[key] !== (current as any)[key]) {
                        setValue(key as any, (next as any)[key], { shouldDirty: true })
                      }
                    })
                  }}
                  checked={state.addressResidenceCopyFromFactual}
                  disabled={state.addressResidenceCopyFromRegistered}
                />
                <label htmlFor="copyInput_res_fact">
                  {accountForm.fiscalAddressFromFactualCopyBtnDesc[lang]}
                </label>
              </>}
              {state.F_FiscalType !== '1' && <div style={{ width: 16 }} />}
              <input
                data-test-id='residenseAddressCopyFromRegistered'
                id="copyInput_res_reg"
                name="copyInput"
                type="checkBox"
                onChange={e => {
                  const current = getValues()
                  const next = residenceCopyFromRegistered(current as State)
                  Object.keys(next).forEach(key => {
                    if ((next as any)[key] !== (current as any)[key]) {
                      setValue(key as any, (next as any)[key], { shouldDirty: true })
                    }
                  })
                }}
                checked={state.addressResidenceCopyFromRegistered}
                disabled={state.addressResidenceCopyFromFactual}
              />
              <label htmlFor="copyInput_res_reg">
                {accountForm.fiscalAddressFactualCopyBtnDesc[lang]}
              </label>
            </div>
            <div className="col-4 col-xs-12">
              {/* RESIDENCE ADDRESS COUNTRY */}
              <SelectInput
                testName={'addressResidenceCountry'}
                testErrorName={'error-addressResidenceCountry'}
                noOptionsMessage={accountForm.citizenshipInput.placeholder}
                lang={lang}
                disabled={!state.agreed || isResidenceAddressDisabled}
                content={accountForm.addressCountry}
                options={addressCountries}
                onChange={id => {
                  setValue('addressResidenceCountry', id, { shouldDirty: true })
                  setValue('addressResidenceRegion', '', { shouldDirty: true })
                  setValue('addressResidenceCity', '', { shouldDirty: true })
                }}
                valid={
                  !errors.addressResidenceCountry &&
                  validateString(state.addressResidenceCountry, 3, false)
                }
                value={state.addressResidenceCountry}
                prefix={
                  state.iframeCountry === 'kz'
                    ? 'urResidenceAddressCountry'
                    : 'urResidenceAddressCountryRf'
                }
              />
            </div>
            <div className="col-4 col-xs-12">
              {/* RESIDENCE ADDRESS REGION */}
              {regionResidenceList.length > 0 ? (
                <SelectInput
                  testName={'addressResidenceRegion'}
                  testErrorName={'error-addressResidenceRegion'}
                  disabled={isResidenceAddressDisabled}
                  className={styles.input}
                  lang={lang}
                  noOptionsMessage={accountForm.addressRegion.placeholder}
                  content={accountForm.addressRegion}
                  value={state.addressResidenceRegion}
                  options={regionResidenceList}
                  valid={
                    state.addressResidenceRegion.length > 0
                      ? smartValidateString(state.addressResidenceRegion) &&
                      !errors.addressResidenceRegion
                      : !errors.addressResidenceRegion
                  }
                  onChange={addressResidenceRegion => {
                    setValue('addressResidenceRegion', addressResidenceRegion, { shouldDirty: true })
                    setValue('addressResidenceCity', '', { shouldDirty: true })
                  }}
                  prefix={
                    state.addressResidenceCountry === '398'
                      ? 'urResidenceAddressRegion' :
                      state.addressResidenceCountry === '643'
                        ? 'urResidenceAddressRegionRf'
                        : undefined
                  }
                />
              ) : (
                <TextInput
                  testName="addressResidenceRegion"
                  testErrorName="error-addressResidenceRegion"
                  disabled={!state.agreed || isResidenceAddressDisabled}
                  lang={lang}
                  content={accountForm.addressRegion}
                  onChange={addressResidenceRegion => {
                    setValue('addressResidenceRegion', addressResidenceRegion, { shouldDirty: true })
                  }}
                  valid={
                    state.addressResidenceRegion.length > 0
                      ? smartValidateString(state.addressResidenceRegion) &&
                      !errors.addressResidenceRegion
                      : !errors.addressResidenceRegion
                  }
                  value={state.addressResidenceRegion}
                />
              )}
            </div>
            <div className="col-4 col-xs-12">
              {/* RESIDENCE ADDRESS CITY */}
              {regionCityResidence ? (
                <SelectInput
                  testName={'addressResidenceCity'}
                  testErrorName={'error-addressResidenceCity'}
                  className={styles.input}
                  disabled={!state.addressResidenceRegion || isResidenceAddressDisabled}
                  lang={lang}
                  noOptionsMessage={accountForm.addressCity.placeholder}
                  content={accountForm.addressCity}
                  value={state.addressResidenceCity}
                  optionValue={{
                    value: state.addressResidenceCity,
                    label: state.addressResidenceCity
                  }}
                  options={cityResidenceList}
                  valid={
                    state.addressResidenceCity.length > 0
                      ? smartValidateString(state.addressResidenceCity) &&
                      !errors.addressResidenceCity
                      : !errors.addressResidenceCity
                  }
                  onChange={addressResidenceCity => {
                    setValue('addressResidenceCity', addressResidenceCity, { shouldDirty: true })
                  }}
                  onCreateOption={(addressResidenceCity: any) => {
                    console.log('addressResidenceCity', addressResidenceCity)
                    setValue('addressResidenceCity', addressResidenceCity, { shouldDirty: true })
                  }}
                  prefix={
                    state.addressRegisteredCountry === '398' && ['Алматинская область', 'Алматы облысы', 'Almaty'].includes(state.addressResidenceRegion)
                      ? 'urResidenceAddressCity'
                      : state.addressResidenceCountry === '643' && ['Москва и Московская обл.', 'Мәскеу және Мәскеу облысы', 'Moscow and Moscow region', 'Санкт-Петербург и область', 'Санкт-Петербург және облыс', 'St.Petersburg and Leningrad region'].includes(state.addressResidenceRegion)
                        ? 'urResidenceAddressCityRf'
                        : undefined
                  }
                  isSearchable={true}
                  creatable={true}
                />
              ) : (
                <TextInput
                  testName="addressResidenceCity"
                  testErrorName="error-addressResidenceCity"
                  disabled={isResidenceAddressDisabled}
                  lang={lang}
                  content={accountForm.addressCity}
                  onChange={addressResidenceCity => {
                    setValue('addressResidenceCity', addressResidenceCity, { shouldDirty: true })
                  }}
                  valid={
                    validateString(state.addressResidenceCity, 300) &&
                    !errors.addressResidenceCity
                  }
                  value={state.addressResidenceCity}
                  required
                />
              )}
            </div>
            <div className="col-3 col-xs-12">
              {/* RESIDENCE ADDRESS STREET */}
              <TextInput
                testName="addressResidenceStreet"
                testErrorName="error-addressResidenceStreet"
                disabled={!state.agreed || isResidenceAddressDisabled}
                lang={lang}
                content={accountForm.addressStreet}
                onChange={addressResidenceStreet => {
                  setValue('addressResidenceStreet', addressResidenceStreet, { shouldDirty: true })
                }}
                valid={
                  state.addressResidenceStreet.length > 0
                    ? smartValidateString(state.addressResidenceStreet) &&
                    !errors.addressResidenceStreet
                    : !errors.addressResidenceStreet
                }
                value={state.addressResidenceStreet}
              />
            </div>
            <div className="col-1 col-xs-12">
              {/* RESIDENCE ADDRESS BUILDING */}
              <TextInput
                testName="addressResidenceBuilding"
                testErrorName="error-addressResidenceBuilding"
                disabled={!state.agreed || isResidenceAddressDisabled}
                lang={lang}
                content={accountForm.addressBuilding}
                onChange={addressResidenceBuilding => {
                  setValue('addressResidenceBuilding', addressResidenceBuilding, { shouldDirty: true })
                }}
                valid={
                  validateString(state.addressResidenceBuilding, 300) &&
                  !errors.addressResidenceBuilding
                }
                value={state.addressResidenceBuilding}
                required
              />
            </div>
            <div className="col-1 col-xs-12">
              {/* RESIDENCE ADDRESS BUILDING BLOCK */}
              <TextInput
                testName="addressResidenceBuildingBlock"
                testErrorName="error-addressResidenceBuildingBlock"
                disabled={!state.agreed || isResidenceAddressDisabled}
                lang={lang}
                content={accountForm.addressBuildingBlock}
                onChange={addressResidenceBuildingBlock => {
                  setValue('addressResidenceBuildingBlock', addressResidenceBuildingBlock, { shouldDirty: true })
                }}
                valid={
                  validateString(state.addressResidenceBuildingBlock, 300) &&
                  !errors.addressResidenceBuildingBlock
                }
                value={state.addressResidenceBuildingBlock}
                required
              />
            </div>
            <div className="col-1 col-xs-12">
              {/* RESIDENCE ADDRESS APARTMENTS */}
              <TextInput
                testName="addressResidenceApartments"
                testErrorName="error-addressResidenceApartments"
                disabled={!state.agreed || isResidenceAddressDisabled}
                lang={lang}
                content={accountForm.addressApartments}
                onChange={addressResidenceApartments => {
                  setValue('addressResidenceApartments', addressResidenceApartments, { shouldDirty: true })
                }}
                valid={
                  validateString(state.addressResidenceApartments, 50) &&
                  !errors.addressResidenceApartments
                }
                required
                value={state.addressResidenceApartments}
              />
            </div>
            <div className="col-2 col-xs-12">
              {/* RESIDENCE POST CODE */}
              <TextInput
                testName="addressResidencePostCode"
                testErrorName="error-addressResidencePostCode"
                disabled={!state.agreed || isResidenceAddressDisabled}
                lang={lang}
                content={accountForm.F_Adress_Index}
                onChange={addressResidencePostCode => {
                  setValue('addressResidencePostCode', addressResidencePostCode.replace(/_/gim, ''), { shouldDirty: true })
                }}
                mask={'*******'}
                valid={
                  validateString(state.addressResidencePostCode, 50) &&
                  !errors.addressResidencePostCode
                }
                required
                value={state.addressResidencePostCode}
              />
            </div>
          </div>
        </div> : null}
    </>
  )
}
